import { mobileFaz8En } from './mobile.faz8.en';
/** Android management app (phase 10) — mobile-only strings. Shared strings use `admin.*` and `common.*`. */
export const mobileEn = {
  ...mobileFaz8En,
  tabs: {
    home: 'Home',
    more: 'More',
  },
  setupRequired: 'Setup required',
  help: {
    longPressHint: 'Long-press for an explanation.',
  },
  errors: {
    network: 'Could not reach the server. Check your internet connection and try again.',
    timeout: 'The server did not respond in time. Try again.',
    forbidden: 'You do not have permission for this action.',
    generic: 'Something unexpected went wrong. Try again.',
  },
  signIn: {
    server: 'Panel address',
    serverShort: 'Panel: {{host}}',
    serverInvalid: 'Enter a valid https address (e.g. panel.zootopia.com.tr).',
    expired: 'Your session expired or was closed from another device. Please sign in again.',
    signedOut: 'Signed out. Session data on this device was deleted.',
    forgotNeedsEmail: 'Enter your email address first to reset your password.',
  },
  mfa: {
    backupLead: 'If you cannot reach your authenticator app, enter one of your backup codes.',
    wrongAccount: 'Signing in with a different account?',
    enrollLead:
      'Two-step verification is required for the panel. Open an authenticator app (Google Authenticator, Microsoft Authenticator, …) on this phone, add the key and enter the 6-digit code it shows.',
    openAuthenticator: 'Open in authenticator app',
    noAuthenticator: 'No authenticator app found. Enter the key in the app manually.',
    backupWriteDown:
      'Write the codes on paper or save them in your password manager. Screenshots are blocked.',
  },
  noOrganization: {
    title: 'Your account is not linked to a business',
    lead: 'Your membership may have been removed or suspended. Ask your business administrator for an invitation.',
  },
  lock: {
    title: 'App locked',
    prompt: 'Unlock Zootopia Panel',
    unlock: 'Unlock',
    failed: 'Verification failed. Try again.',
    setting: 'Biometric lock',
    settingLead: 'Ask for fingerprint, face or screen lock when opening',
    unavailable: 'This device has no biometric authentication.',
    notEnrolled: 'Add a fingerprint or face in phone settings, then turn this on.',
  },
  offline: {
    banner: 'Offline — only the on-device stock count can continue.',
    startup:
      'Could not reach the server; the app opened with your last session. Pull down to refresh when back online.',
  },
  home: {
    today: 'Today',
    ordersToday: 'Orders',
    paidToday: 'Paid',
    paidNote: 'Total of {{count}} orders created today and paid (refunds not deducted).',
    mixedCurrency: 'Payments in other currencies are not included.',
    atLeast: 'At least {{count}}',
    toPrepare: 'To prepare (paid)',
    returns: 'Return requests',
    expiring30: 'Expiring within 30 days',
    paymentReview: 'Payments under review',
    setupOnPanel: 'Setup steps are in the Setup Wizard on the panel.',
    reportsLater:
      'Sales reports and charts arrive with the reports module (phase 8). No estimated numbers are shown without real data.',
  },
  orders: {
    captureNoteHint: 'E.g. receipt number and bank. At least 3 characters.',
    event: 'Activity',
  },
  returns: {
    lead: 'Tap a request; the decision is made on the order screen together with items and payments.',
    empty: 'No requests in this status',
  },
  payments: {
    empty: 'No payments in this state',
    reviewLead:
      'Payments whose amount or status reported by the provider does not match the order. The order is not considered paid; details are on the panel.',
  },
  products: {
    onHand: 'Stock {{count}}',
    edit: 'Edit product',
    longTextOnPanel: 'Long description, options, categories and SEO fields are edited on the panel.',
    reviewedAt: 'Approved on',
    assignBarcode: 'Assign barcode',
    barcodeFormat: 'A barcode must be 8–14 digits.',
    barcodeSaved: 'Barcode saved.',
    barcodeSavedNoGtin:
      'Barcode saved. Warning: the check digit is not a valid GTIN; Google listings may reject it.',
    noPrice: 'No price',
    price: 'Price ({{currency}})',
    priceInvalid: 'Could not read the amount. Example: 1299.90',
    priceEmptyHint: 'Saving an empty value removes the price from this list.',
    priceTaxIncluded: 'Prices in this list include VAT.',
    priceTaxExcluded: 'Prices in this list exclude VAT.',
    newLead:
      'The product is created as a draft and is not shown in the store. Add photos, prices and content; veterinary approval and publishing are on the product screen.',
    created: 'Product created as a draft',
    healthOn: 'Composition, usage and warnings go through veterinary approval.',
    healthOff: 'No health content (e.g. accessory); no veterinary approval needed.',
  },
  photo: {
    camera: 'Take photo',
    library: 'From gallery',
    uploading: 'Uploading image…',
    permission: 'Camera permission was not granted. You can allow it in phone settings.',
    tooLarge: 'The image is larger than 5 MB. Choose a smaller one.',
  },
  scan: {
    title: 'Scan barcode',
    button: 'Scan barcode',
    aim: 'Place the barcode inside the frame',
    manual: 'Type the code',
    permissionTitle: 'Camera permission needed',
    permissionLead:
      'The camera is used to scan barcodes. Images are processed on the device and never sent anywhere.',
    allow: 'Allow camera',
    permissionDenied: 'Camera permission is off. You can turn it on in phone settings.',
    openSettings: 'Open settings',
  },
  stock: {
    notFound: 'No product found for "{{code}}".',
    expired: 'Expired',
    adjustmentHint: 'Positive to increase, negative (-) to decrease. A reason is required.',
    dateHint: 'DD.MM.YYYY (e.g. 31.12.2027)',
    dateInvalid: 'Could not read the date. Example: 31.12.2027',
    typeHints: {
      receipt: 'New stock entering the warehouse (with lot and expiry).',
      adjustment: 'Correct a wrong record (+/−), with a reason.',
      damage: 'Broken, spoiled or lost items (deducted).',
      count: 'The counted quantity becomes the new total for this warehouse/lot.',
    },
  },
  count: {
    title: 'Barcode stock count',
    start: 'Barcode stock count',
    lead: 'Choose the warehouse to count. The count is kept on the device; you can keep scanning without internet.',
    begin: 'Start count',
    lines: '{{count}} lines',
    startedAt: 'Started: {{date}}',
    offlineNote: 'You are offline. Scanned codes are kept; match and submit them when back online.',
    cameraOn: 'Scan with camera',
    cameraOff: 'Turn camera off',
    manual: 'Type a barcode or SKU',
    notFound: 'No product found for "{{code}}". The product may have no barcode assigned.',
    ambiguous: '"{{code}}" matches more than one product; scan by SKU instead.',
    savedOffline: 'Code saved; it will be matched to a product when online.',
    unresolved: '{{count}} lines are not matched to a product yet.',
    resolve: 'Match now',
    needLot: 'Choose a lot for {{count}} lines.',
    empty: 'Nothing scanned yet',
    emptyLead: 'Turn on the camera and scan product barcodes; each scan adds one.',
    unknownProduct: 'Unmatched code',
    lineSent: 'Submitted · new total {{onHand}}',
    lineError: 'Not submitted',
    lineUnresolved: 'Unmatched',
    lineNeedsLot: 'Choose lot',
    counted: 'Counted',
    pickLot: 'Choose lot',
    changeLot: 'Change lot',
    lotLead:
      'Choose the lot number printed on the packages you counted. The count becomes the new total of that lot.',
    noLots: 'No lot stock found in this warehouse.',
    systemQty: '{{count}} in system',
    submit: 'Submit count ({{count}})',
    sent: '{{count}} lines submitted.',
    reason: 'Mobile count {{date}}',
    discard: 'Discard count',
    discardConfirm: 'The count on this device will be deleted. Submitted lines stay recorded. Continue?',
  },
  review: {
    title: 'Awaiting expert review',
    detailTitle: 'Expert review',
    empty: 'Nothing is awaiting review',
    count: '{{count}} items await expert review.',
    storeNote: 'Pages, posts, banners and questions are listed for the "{{store}}" store.',
    inField: 'Field: {{field}}',
    content: 'Content',
    hashNote:
      'Approval is tied to the content as it is now. If it changes later it needs approval again; if it changes meanwhile the decision is rejected and you are asked to refresh.',
    noPermission: 'You do not have permission to decide on this content.',
    kinds: {
      product: 'Product',
      post: 'Blog post',
      page: 'Page',
      banner: 'Banner',
      question: 'Product question',
    },
    bannerFields: {
      title: 'Title',
      text: 'Text',
      ctaLabel: 'Button text',
    },
  },
  seo: {
    title: 'SEO summary',
    noStore: 'Select a store to see the SEO summary.',
    fixOnPanel: 'Fix product titles and descriptions on the product screen, others on the panel.',
  },
  more: {
    paymentsHint: 'Payments under review, pending and failed',
    reviewHint: 'For the veterinary reviewer',
    version: 'Version {{version}} · {{server}}',
  },
  notifications: {
    title: 'Push notifications',
    channel: 'Orders and operations',
    lead: 'This phone is notified of events such as a new order, a payment review or a return request. Only events you have permission for are sent; notifications contain no customer data.',
    device: 'This phone',
    on: 'On',
    off: 'Off',
    onLead: 'This phone is registered to receive notifications.',
    askLead: 'Allow notifications on this phone so you do not miss new orders.',
    deniedLead: 'Notification permission was not granted. Press the button to be asked again.',
    blockedLead:
      'Notifications are turned off in the phone settings. Turn them on for Zootopia Panel under Settings → Notifications.',
    notRegisteredLead:
      'Notifications are allowed but this phone is not registered (it was removed or registration failed).',
    allow: 'Turn on notifications',
    openSettings: 'Open phone settings',
    registered: 'Notifications are on for this phone.',
    registerFailed:
      'This phone could not be registered. Check your internet connection and try again; if it persists, update Google Play services.',
    removeThisConfirm:
      'Remove this phone? It will not receive notifications; press "Turn on notifications" to turn them back on.',
    deviceDisabled: 'Delivery failing',
    unsupported: 'Push notifications only work in the Android app.',
    setupTitle: 'This version has no notification setup',
    setupLead:
      'This version of the app was built without the Firebase configuration, so notifications cannot be sent to this phone. An administrator needs to add the configuration on GitHub and build a new version (Android build guide → Push notifications).',
    meanwhile: 'Until then the Home screen shows what needs attention; pull down to refresh.',
  },
  settings: {
    title: 'Settings',
    signOutConfirm: 'The session on this device will be closed and its data deleted.',
    signOutPendingCount:
      'There are {{count}} unsubmitted count lines on this device; they will be deleted. Sign out anyway?',
  },
  organization: {
    switchTitle: 'Switch business',
    switchConfirm: 'Cached data and any unsubmitted count on this device will be deleted. Continue?',
  },
};
